import { useEffect, useRef, useState } from "preact/hooks";

const MARGIN_TOP_CONTROL_PANEL = 22;
const ADAPTIVE_BREAKPOINT = 992;
const STATIC_CANVAS_HEIGHT = 220;
const ANDROID_PADDING = 20;

export const useResize = () => {
  const [shouldUnlockScroll, setShouldUnlockScroll] = useState(false);
  const [isGameLoading, setIsGameLoading] = useState<boolean>(true);
  const [canvasWidth, setCanvasWidth] = useState<number>();
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [canvasHeight, setCanvasHeight] =
    useState<number>(STATIC_CANVAS_HEIGHT);
  const [isAndroid, setIsAndroid] = useState(false);

  const headerRef = useRef<HTMLElement | null>(null);
  const scoresRef = useRef<HTMLDivElement | null>(null);
  const controllersRef = useRef<HTMLDivElement | null>(null);
  const gameContainerRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const prevIsMobileRef = useRef<boolean | null>(null);

  const isWaitingCanvasSize = !canvasWidth || !canvasHeight;

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined")
      return;
    const vw = window.innerWidth;

    const shouldLock =
      isGameLoading ||
      (vw < ADAPTIVE_BREAKPOINT && !isOpenDetails && !shouldUnlockScroll);

    document.documentElement.style.overflow = shouldLock ? "hidden" : "";
    document.body.style.overflow = shouldLock ? "hidden" : "";
    document.body.classList.toggle("allow-scroll", !shouldLock);

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.classList.remove("allow-scroll");
    };
  }, [isGameLoading, isOpenDetails, shouldUnlockScroll]);

  useEffect(() => {
    const measure = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const nextIsMobile = vw < ADAPTIVE_BREAKPOINT;
      const isAndroidDevice =
        typeof navigator !== "undefined" &&
        /Android/i.test(navigator.userAgent);
      setIsAndroid(isAndroidDevice);

      if (
        prevIsMobileRef.current === null ||
        prevIsMobileRef.current !== nextIsMobile
      ) {
        if (nextIsMobile) {
          setIsOpenDetails(false);
        } else {
          setIsOpenDetails(true);
        }
        prevIsMobileRef.current = nextIsMobile;
      }

      const containerWidth = gameContainerRef.current?.clientWidth || vw;
      setCanvasWidth(containerWidth);

      // Calculate padding to account for
      const paddingToAccount =
        nextIsMobile && isAndroidDevice ? ANDROID_PADDING : 0;

      if (nextIsMobile) {
        const headerH = headerRef.current?.offsetHeight || 0;
        const scoresH = scoresRef.current?.offsetHeight || 0;
        const controlsH = controllersRef.current?.offsetHeight || 0;
        const arrowH = arrowRef.current?.offsetHeight || 0;

        const minTotalHeight =
          headerH +
          scoresH +
          controlsH +
          arrowH +
          MARGIN_TOP_CONTROL_PANEL +
          STATIC_CANVAS_HEIGHT +
          paddingToAccount;

        if (vh < minTotalHeight) {
          setShouldUnlockScroll(true);
          setCanvasHeight(STATIC_CANVAS_HEIGHT);
        } else {
          setShouldUnlockScroll(false);
          const availableH = Math.max(
            vh -
              headerH -
              scoresH -
              controlsH -
              arrowH -
              MARGIN_TOP_CONTROL_PANEL -
              paddingToAccount,
            STATIC_CANVAS_HEIGHT
          );
          setCanvasHeight(availableH);
        }
      } else {
        const headerH = headerRef.current?.offsetHeight || 0;
        const scoresH = scoresRef.current?.offsetHeight || 0;
        const controlsH = controllersRef.current?.offsetHeight || 0;
        const availableH = Math.max(
          vh - headerH - scoresH - controlsH - MARGIN_TOP_CONTROL_PANEL,
          200
        );
        setShouldUnlockScroll(false);
        setCanvasHeight(availableH);
      }
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (gameContainerRef.current) ro.observe(gameContainerRef.current);
    if (headerRef.current) ro.observe(headerRef.current);
    if (scoresRef.current) ro.observe(scoresRef.current);
    if (controllersRef.current) ro.observe(controllersRef.current);

    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isOpenDetails]);
  return {
    canvasWidth,
    canvasHeight,
    headerRef,
    scoresRef,
    controllersRef,
    gameContainerRef,
    arrowRef,
    isFullyLoading: isGameLoading || isWaitingCanvasSize,
    isWaitingCanvasSize,
    isOpenDetails,
    isAndroid,
    setIsGameLoading,
    setIsOpenDetails,
  };
};
